import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { leveledContent } from '../../config/consequencesRecommendations';
import './Recommendations.css';

// Importamos íconos específicos para cada tipo de recomendación
const recommendationIcons = {
  mindfulness: '🧘', // Prácticas de mindfulness
  breath: '🫁', // Respiración
  physical: '🚶', // Actividad física
  cognitive: '🧠', // Estrategias cognitivas
  emotional: '❤️', // Regulación emocional
  social: '👥', // Relaciones sociales
  lifestyle: '🌱', // Estilo de vida
  professional: '🩺', // Ayuda profesional
  sleep: '😴', // Sueño
};

const Recommendations = ({ areaId, level, color }) => {
  const [symptomSpecificContent, setSymptomSpecificContent] = useState([]);
  
  useEffect(() => {
    // Buscamos contenido específico por área y nivel en el archivo de configuración
    if (leveledContent[areaId.toLowerCase()] && 
        leveledContent[areaId.toLowerCase()][level.toUpperCase()] && 
        leveledContent[areaId.toLowerCase()][level.toUpperCase()].recommendations) {
      
      // Usamos las recomendaciones específicas para este tipo de sintomatología y nivel
      setSymptomSpecificContent(
        leveledContent[areaId.toLowerCase()][level.toUpperCase()].recommendations.map(text => ({
          text,
          // Asignar un tipo basado en el contenido del texto
          type: text.includes('psicólogo') || text.includes('profesional') || text.includes('terapeuta') || text.includes('especialista') ? 'professional' :
                text.includes('meditación') || text.includes('atención plena') || text.includes('mindfulness') || text.includes('consciente') ? 'mindfulness' :
                text.includes('respiración') || text.includes('diafragmática') ? 'breath' :
                text.includes('ejercicio') || text.includes('físic') || text.includes('caminar') || text.includes('movimiento') ? 'physical' :
                text.includes('pensamientos') || text.includes('cognitiv') || text.includes('identifica') || text.includes('cuestiona') ? 'cognitive' :
                text.includes('emocional') || text.includes('compasión') ? 'emotional' :
                text.includes('social') || text.includes('compart') || text.includes('relacion') ? 'social' :
                text.includes('sueño') || text.includes('dormir') ? 'sleep' : 'lifestyle'
        }))
      );
    } else {
      // Si no hay contenido específico, usar una función de respaldo
      const simulatedPercentage = level === "LEVE" ? 35 : level === "MODERADO" ? 55 : level === "ALTO" ? 85 : 37;
      setSymptomSpecificContent(getRecommendationsByLevel(simulatedPercentage, areaId.toLowerCase()));
    }
  }, [areaId, level]);
  
  // Función para obtener recomendaciones según el nivel del termómetro y la sintomatología
  const getRecommendationsByLevel = (percentage, symptomType) => {
    // Determinar nivel según porcentaje
    let sufferingLevel;
    if (percentage < 20) {
      sufferingLevel = "bajo";
    } else if (percentage < 40) {
      sufferingLevel = "leve";
    } else if (percentage < 60) {
      sufferingLevel = "moderado";
    } else if (percentage < 80) {
      sufferingLevel = "significativo";
    } else {
      sufferingLevel = "intenso";
    }
    
    // Recomendaciones según el nivel y el área, categorizadas por tipo
    const recommendationsByArea = {
      ansiedad: {
        bajo: [
          { text: "Práctica diaria de atención plena: dedica 5 minutos a observar tu respiración sin juzgar", type: "mindfulness" },
          { text: "Incorpora 5-10 minutos diarios de respiración consciente, inhalando por la nariz y exhalando lentamente por la boca", type: "breath" },
          { text: "Practica estar presente en actividades cotidianas, prestando total atención a lo que haces en cada momento", type: "mindfulness" },
          { text: "Mantén un nivel moderado de actividad física, preferiblemente al aire libre", type: "physical" },
          { text: "Identifica tus principales desencadenantes de ansiedad y regístralos en un diario", type: "cognitive" }
        ],
        leve: [
          { text: "Meditación guiada de atención plena para la ansiedad durante 10-15 minutos diarios", type: "mindfulness" },
          { text: "Practica técnicas de respiración diafragmática durante 10 minutos, dos veces al día", type: "breath" },
          { text: "Identifica y limita los estimulantes como cafeína, azúcar y alcohol", type: "lifestyle" },
          { text: "Establece una rutina de desconexión digital 1 hora antes de dormir", type: "lifestyle" },
          { text: "Realiza un escaneo corporal diario para identificar tensiones y liberar conscientemente", type: "mindfulness" },
          { text: "Incorpora ejercicio cardiovascular moderado al menos 3 veces por semana", type: "physical" },
          { text: "Aprende a identificar pensamientos ansiosos y cuestionarlos con evidencia", type: "cognitive" }
        ],
        moderado: [
          { text: "Practica la meditación de atención plena centrada en sensaciones físicas de ansiedad sin reaccionar", type: "mindfulness" },
          { text: "Incorpora la práctica diaria de relajación muscular progresiva completa (15-20 minutos)", type: "physical" },
          { text: "Establece un tiempo limitado (15 minutos) para 'preocuparte' conscientemente, fuera de este tiempo, posterga las preocupaciones", type: "cognitive" },
          { text: "Identifica los pensamientos anticipatorios negativos y práctica su cuestionamiento sistemático", type: "cognitive" },
          { text: "Dedica tiempo a actividades que te conecten con el momento presente (arte, naturaleza, movimiento)", type: "mindfulness" },
          { text: "Considera consultar con un profesional de la salud mental especializado en ansiedad", type: "professional" },
          { text: "Practica la técnica 5-4-3-2-1 para momentos de ansiedad aguda (5 cosas que ves, 4 que tocas, 3 que oyes, 2 que hueles, 1 que saboreas)", type: "mindfulness" },
          { text: "Aprende y practica técnicas de exposición gradual a situaciones temidas", type: "cognitive" }
        ],
        significativo: [
          { text: "Busca apoyo profesional con un psicólogo especializado en trastornos de ansiedad", type: "professional" },
          { text: "Practica la meditación RAIN (Reconocer, Aceptar, Investigar, No-identificación) cuando aparezca la ansiedad", type: "mindfulness" },
          { text: "Establece rutinas diarias de meditación guiada específica para ansiedad (20-30 minutos)", type: "mindfulness" },
          { text: "Implementa un diario de ansiedad para identificar desencadenantes y patrones", type: "cognitive" },
          { text: "Practica la exposición gradual a situaciones que evitas por ansiedad con apoyo profesional", type: "cognitive" },
          { text: "Establece rutinas firmes de sueño, alimentación y ejercicio", type: "lifestyle" },
          { text: "Desarrolla una práctica diaria de autocompasión consciente para momentos de ansiedad intensa", type: "emotional" },
          { text: "Aprende y practica técnicas de defusión cognitiva para distanciarte de pensamientos ansiosos", type: "cognitive" },
          { text: "Considera la posibilidad de unirte a un grupo de apoyo de personas con ansiedad", type: "social" }
        ],
        intenso: [
          { text: "Busca ayuda profesional inmediata con un psicólogo o psiquiatra especializado en trastornos de ansiedad", type: "professional" },
          { text: "Practica la meditación de anclaje al momento presente varias veces al día (5 min)", type: "mindfulness" },
          { text: "Aprende técnicas de contención para momentos de crisis (5-4-3-2-1, técnicas de grounding)", type: "cognitive" },
          { text: "Diseña con ayuda profesional un plan de seguridad para momentos de crisis aguda", type: "professional" },
          { text: "Refuerza tu red de apoyo y comparte tu situación con personas de confianza", type: "social" },
          { text: "Sigue rigurosamente las recomendaciones del profesional respecto a tratamiento", type: "professional" },
          { text: "Practica ejercicios de conciencia corporal para reconectar con el momento presente durante crisis", type: "mindfulness" },
          { text: "Aprende a reconocer los primeros signos físicos de un ataque de ansiedad para intervenir temprano", type: "physical" },
          { text: "Simplifica temporalmente tus responsabilidades para centrarte en tu recuperación", type: "lifestyle" },
          { text: "Considera la terapia cognitivo-conductual como tratamiento de primera línea", type: "professional" }
        ]
      },
      tristeza: {
        bajo: [
          { text: "Dedica 5 minutos diarios a meditar enfocándote en sensaciones corporales sin juicio", type: "mindfulness" },
          { text: "Incrementa ligeramente tu exposición a la luz natural durante el día", type: "lifestyle" },
          { text: "Dedica tiempo a actividades que generen pequeñas satisfacciones diarias", type: "emotional" },
          { text: "Practica la atención plena durante actividades cotidianas como comer o caminar", type: "mindfulness" },
          { text: "Mantén conexiones sociales significativas, aunque sean breves", type: "social" },
          { text: "Incorpora una rutina de ejercicio suave como caminar 20 minutos diarios", type: "physical" }
        ],
        leve: [
          { text: "Establece una práctica diaria de meditación consciente de 10-15 minutos", type: "mindfulness" },
          { text: "Programa al menos una actividad placentera cada día, por pequeña que sea", type: "emotional" },
          { text: "Mantén una rutina regular de ejercicio moderado, preferiblemente por la mañana", type: "physical" },
          { text: "Practica la gratitud consciente mediante un diario o reflexión antes de dormir", type: "mindfulness" },
          { text: "Asegura una adecuada higiene del sueño con horarios regulares", type: "lifestyle" },
          { text: "Practica la observación sin juicio de pensamientos tristes, viéndolos como eventos mentales pasajeros", type: "mindfulness" },
          { text: "Busca oportunidades de conexión social significativa al menos tres veces por semana", type: "social" },
          { text: "Limita la exposición a noticias negativas y redes sociales que puedan afectar tu estado de ánimo", type: "lifestyle" }
        ],
        moderado: [
          { text: "Incorpora la meditación de autocompasión en tu rutina diaria (15-20 min)", type: "mindfulness" },
          { text: "Establece pequeñas metas diarias alcanzables y celébralas cuando las consigas", type: "cognitive" },
          { text: "Practica técnicas de autocompasión y diálogo interno positivo", type: "emotional" },
          { text: "Mantén contacto social regular, incluso cuando no te apetezca", type: "social" },
          { text: "Dedica tiempo diario a la observación consciente de emociones sin identificarte con ellas", type: "mindfulness" },
          { text: "Considera buscar apoyo profesional con un psicólogo o terapeuta especializado", type: "professional" },
          { text: "Incorpora ejercicio regular en tu rutina, preferiblemente actividades grupales", type: "physical" },
          { text: "Aprende técnicas de activación conductual para combatir la inercia", type: "behavioral" },
          { text: "Identifica y cuestiona pensamientos automáticos negativos", type: "cognitive" },
          { text: "Asegura una exposición adecuada a la luz natural, especialmente por las mañanas", type: "lifestyle" }
        ],
        significativo: [
          { text: "Busca ayuda profesional con un psicólogo especializado en estados depresivos", type: "professional" },
          { text: "Práctica diaria de meditación guiada enfocada en aceptación y autocompasión (20-30 min)", type: "mindfulness" },
          { text: "Establece una estructura diaria predecible con rutinas y horarios consistentes", type: "lifestyle" },
          { text: "Activa tu cuerpo aunque no tengas motivación, comenzando con pequeños paseos", type: "physical" },
          { text: "Comunica tu estado a personas cercanas de confianza para obtener apoyo", type: "social" },
          { text: "Practica la observación mindful de pensamientos negativos sin creerlos ni luchar con ellos", type: "mindfulness" },
          { text: "Reduce la toma de decisiones importantes en este momento", type: "cognitive" },
          { text: "Practica técnicas de activación conductual guiadas por un profesional", type: "professional" },
          { text: "Aprende a identificar los primeros signos de recaída para intervenir temprano", type: "cognitive" },
          { text: "Considera terapias específicas como TCC o MBCT (Terapia Cognitiva Basada en Mindfulness)", type: "professional" }
        ],
        intenso: [
          { text: "Busca ayuda profesional inmediata con un psicólogo y valora la consulta psiquiátrica", type: "professional" },
          { text: "Establece un plan de seguridad con profesionales si existen ideas de hacerte daño", type: "professional" },
          { text: "Practica breves meditaciones de anclaje al momento presente varias veces al día (3-5 min)", type: "mindfulness" },
          { text: "Mantén contacto diario con personas de apoyo designadas", type: "social" },
          { text: "Sigue rigurosamente las recomendaciones de los profesionales de salud mental", type: "professional" },
          { text: "Practica ejercicios de presencia corporal y conexión sensorial para momentos difíciles", type: "mindfulness" },
          { text: "Enfócate en la supervivencia día a día, con objetivos muy pequeños y concretos", type: "cognitive" },
          { text: "Recuerda que incluso la depresión severa es tratable y existe recuperación", type: "emotional" },
          { text: "Simplifica al máximo tu vida para reducir demandas mientras te recuperas", type: "lifestyle" },
          { text: "Busca información sobre programas intensivos especializados en depresión", type: "professional" },
          { text: "Recuerda que este estado, por intenso que sea, es temporal y puede mejorar con tratamiento", type: "emotional" }
        ]
      },
      estres: {
        bajo: [
          { text: "Dedica 5-10 minutos diarios a la meditación de atención a la respiración", type: "mindfulness" },
          { text: "Incorpora mini-pausas de respiración profunda (2-3 minutos) varias veces al día", type: "breath" },
          { text: "Practica la observación consciente de sensaciones de tensión corporal", type: "mindfulness" },
          { text: "Practica la desconexión digital en ciertos momentos del día", type: "lifestyle" },
          { text: "Separa claramente tiempo de trabajo y tiempo personal", type: "lifestyle" },
          { text: "Incorpora pequeños momentos de movimiento entre tareas sedentarias", type: "physical" },
          { text: "Practica técnicas de gestión del tiempo como la técnica Pomodoro", type: "cognitive" }
        ],
        leve: [
          { text: "Establece una práctica diaria de meditación mindfulness de 10-15 minutos", type: "mindfulness" },
          { text: "Practica técnicas de relajación breves (5-10 minutos) al final del día", type: "breath" },
          { text: "Organiza tus tareas por prioridad y aprende a delegar lo que no es esencial", type: "cognitive" },
          { text: "Incorpora momentos de descanso consciente entre actividades exigentes", type: "mindfulness" },
          { text: "Práctica la atención plena durante actividades cotidianas (comidas, duchas, caminar)", type: "mindfulness" },
          { text: "Limita la multitarea y practica la concentración en una sola cosa a la vez", type: "cognitive" },
          { text: "Incorpora ejercicio regular que te permita descargar tensión (3-4 veces por semana)", type: "physical" },
          { text: "Aprende a identificar señales tempranas de estrés en tu cuerpo", type: "physical" },
          { text: "Practica decir 'no' a compromisos no esenciales", type: "social" }
        ],
        moderado: [
          { text: "Incorpora meditaciones específicas para el estrés (15-20 min diarios)", type: "mindfulness" },
          { text: "Dedica tiempo diario a la práctica corporal consciente (yoga, tai chi, chi kung)", type: "mindfulness" },
          { text: "Revisa y ajusta tus compromisos, aprendiendo a establecer límites saludables", type: "lifestyle" },
          { text: "Programa tiempo diario para actividades de autocuidado (no negociables)", type: "emotional" },
          { text: "Practica la relajación progresiva o meditación guiada por 15-20 minutos diarios", type: "mindfulness" },
          { text: "Identifica y cuestiona pensamientos perfeccionistas o autoexigentes", type: "cognitive" },
          { text: "Incorpora la práctica formal del 'no hacer' al menos una vez al día", type: "mindfulness" },
          { text: "Considera buscar apoyo profesional especializado en manejo del estrés", type: "professional" },
          { text: "Evalúa tu entorno y elimina o minimiza fuentes de estrés innecesarias", type: "lifestyle" },
          { text: "Establece límites claros con personas que aumentan tu nivel de estrés", type: "social" },
          { text: "Aprende técnicas de comunicación asertiva para reducir conflictos", type: "social" }
        ],
        significativo: [
          { text: "Busca ayuda profesional con un especialista en manejo del estrés", type: "professional" },
          { text: "Practica diariamente meditaciones formales de atención plena (20-30 min)", type: "mindfulness" },
          { text: "Evalúa seriamente reorganizar tus responsabilidades y compromisos actuales", type: "lifestyle" },
          { text: "Establece límites firmes en todos los ámbitos de tu vida", type: "emotional" },
          { text: "Practica técnicas diarias de reducción del estrés (yoga, meditación, tai chi)", type: "physical" },
          { text: "Incorpora prácticas diarias de autocompasión y aceptación consciente", type: "mindfulness" },
          { text: "Identifica las fuentes principales de estrés y elabora un plan específico", type: "cognitive" },
          { text: "Considera la necesidad de cambios estructurales en tu vida laboral o personal", type: "lifestyle" },
          { text: "Aprende a delegar efectivamente tanto en el ámbito personal como profesional", type: "social" },
          { text: "Consulta sobre la posibilidad de técnicas complementarias como biofeedback", type: "professional" },
          { text: "Implementa estrategias de comunicación asertiva para manejar conflictos", type: "social" }
        ],
        intenso: [
          { text: "Busca ayuda profesional inmediata especializada en estrés crónico y burnout", type: "professional" },
          { text: "Practica meditaciones breves de anclaje (5 min) varias veces al día", type: "mindfulness" },
          { text: "Evalúa con un profesional la necesidad de un periodo de baja o descanso", type: "professional" },
          { text: "Implementa un plan integral de recuperación con ayuda profesional", type: "professional" },
          { text: "Realiza cambios significativos en tu carga de responsabilidades", type: "lifestyle" },
          { text: "Incorpora la práctica de la atención plena como base para reconectar con tu cuerpo", type: "mindfulness" },
          { text: "Prioriza absolutamente tu recuperación sobre otras obligaciones", type: "emotional" },
          { text: "Sigue un programa estructurado de reducción de estrés bajo supervisión profesional", type: "professional" },
          { text: "Recupera funciones básicas (sueño, alimentación, movimiento) antes de retomar responsabilidades", type: "physical" },
          { text: "Desarrolla un plan de prevención de recaídas con ayuda profesional", type: "professional" },
          { text: "Considera la posibilidad de cambios profundos en tu estilo de vida o carrera", type: "lifestyle" }
        ]
      },
      autoestima: {
        bajo: [
          { text: "Dedica 5-10 minutos diarios a la meditación de autocompasión", type: "mindfulness" },
          { text: "Practica identificar cualidades personales positivas de forma regular", type: "cognitive" },
          { text: "Establece pequeños desafíos que te permitan construir confianza gradualmente", type: "behavioral" },
          { text: "Observa tu diálogo interno sin juicio, notando cuando es negativo", type: "mindfulness" },
          { text: "Practica la gratitud consciente hacia ti mismo/a y tus logros diarios", type: "mindfulness" },
          { text: "Aprende a recibir cumplidos sin restarles importancia", type: "social" },
          { text: "Crea un registro de tus éxitos, por pequeños que sean", type: "cognitive" }
        ],
        leve: [
          { text: "Establece una práctica diaria de meditación de autocompasión (10-15 min)", type: "mindfulness" },
          { text: "Lleva un registro diario de logros y cualidades positivas, por pequeños que sean", type: "cognitive" },
          { text: "Practica el diálogo interno compasivo, hablándote como lo harías a un amigo", type: "cognitive" },
          { text: "Establece límites saludables en relaciones que minan tu confianza", type: "social" },
          { text: "Cultiva actividades que refuercen tu sentido de competencia y disfrute", type: "behavioral" },
          { text: "Practica la observación consciente de tus autocríticas sin identificarte con ellas", type: "mindfulness" },
          { text: "Identifica y cuestiona creencias limitantes sobre ti mismo", type: "cognitive" },
          { text: "Dedica tiempo a desarrollar una habilidad que disfrutes para aumentar tu confianza", type: "behavioral" },
          { text: "Aprende a expresar tus necesidades y opiniones de forma asertiva", type: "social" }
        ],
        moderado: [
          { text: "Dedica 15-20 minutos diarios a prácticas formales de mindfulness y autocompasión", type: "mindfulness" },
          { text: "Aprende a identificar y cuestionar creencias limitantes sobre ti mismo/a", type: "cognitive" },
          { text: "Practica la autocompasión consciente mediante ejercicios estructurados", type: "mindfulness" },
          { text: "Busca ambientes y relaciones que validen y apoyen tu crecimiento", type: "social" },
          { text: "Desarrolla habilidades en áreas que te interesen para construir confianza", type: "behavioral" },
          { text: "Practica el 'no hacer' como forma de valorarte más allá de tus logros", type: "mindfulness" },
          { text: "Considera buscar apoyo profesional especializado en autoestima", type: "professional" },
          { text: "Identifica el origen de tus creencias negativas sobre ti mismo/a", type: "cognitive" },
          { text: "Practica la defusión cognitiva para distanciarte de pensamientos autocríticos", type: "cognitive" },
          { text: "Aprende a recibir críticas constructivas sin personalizar demasiado", type: "emotional" },
          { text: "Establece límites firmes en relaciones donde no te sientes valorado/a", type: "social" }
        ],
        significativo: [
          { text: "Busca ayuda profesional especializada en autoestima y autoconcepto", type: "professional" },
          { text: "Practica diariamente meditaciones específicas de autocompasión (20-30 min)", type: "mindfulness" },
          { text: "Trabaja con un profesional para identificar el origen de creencias negativas", type: "professional" },
          { text: "Practica diariamente técnicas de autoafirmación y autocompasión estructuradas", type: "emotional" },
          { text: "Revisa completamente tus relaciones, alejándote de aquellas que sean tóxicas", type: "social" },
          { text: "Aprende a reconocer y celebrar tus logros sin minimizarlos", type: "cognitive" },
          { text: "Incorpora la práctica de presencia consciente ante autocríticas intensas", type: "mindfulness" },
          { text: "Desarrolla una comprensión de tu valía independiente de logros externos", type: "cognitive" },
          { text: "Participa en grupos de apoyo o terapia grupal para problemas de autoestima", type: "social" },
          { text: "Trabaja en la reparación de tu diálogo interno con ayuda profesional", type: "professional" },
          { text: "Practica técnicas específicas para manejar el perfeccionismo", type: "cognitive" }
        ],
        intenso: [
          { text: "Busca ayuda profesional inmediata especializada en terapias para autoestima", type: "professional" },
          { text: "Dedica tiempo diario a prácticas de mindfulness y autocompasión (divididas en sesiones cortas)", type: "mindfulness" },
          { text: "Trabaja con un profesional para abordar posibles traumas o experiencias pasadas", type: "professional" },
          { text: "Sigue un programa estructurado de reconstrucción de autoconcepto", type: "professional" },
          { text: "Practica el autocuidado básico como acto de autorespeto fundamental", type: "emotional" },
          { text: "Aprende a identificar y confrontar la autocrítica destructiva con ayuda profesional", type: "cognitive" },
          { text: "Practica meditaciones breves de anclaje cuando la autocrítica sea intensa", type: "mindfulness" },
          { text: "Construye gradualmente un nuevo sistema de creencias sobre ti mismo/a", type: "cognitive" },
          { text: "Recuerda que la recuperación de la autoestima es un proceso gradual pero posible", type: "emotional" },
          { text: "Considera la terapia de esquemas o EMDR si hay traumas subyacentes", type: "professional" },
          { text: "Evalúa diferentes modalidades terapéuticas con tu profesional", type: "professional" },
          { text: "Establece una red de apoyo firme y confía en ella durante tu proceso", type: "social" }
        ]
      },
      insomnio: {
        bajo: [
          { text: "Establece un horario regular para acostarte y levantarte, incluso los fines de semana", type: "lifestyle" },
          { text: "Practica una rutina relajante antes de dormir (30-60 minutos)", type: "lifestyle" },
          { text: "Limita la cafeína y el alcohol, especialmente en las horas previas al sueño", type: "lifestyle" },
          { text: "Crea un ambiente de sueño óptimo: oscuro, fresco y silencioso", type: "lifestyle" },
          { text: "Practica 5-10 minutos de meditación centrada en la respiración antes de dormir", type: "mindfulness" },
          { text: "Evita las pantallas al menos una hora antes de acostarte", type: "lifestyle" },
          { text: "Observa sin juzgar tus preocupaciones sobre el sueño", type: "mindfulness" }
        ],
        leve: [
          { text: "Implementa técnicas de higiene del sueño de forma sistemática", type: "lifestyle" },
          { text: "Practica respiración profunda y relajación muscular progresiva antes de dormir", type: "breath" },
          { text: "Mantén un diario de sueño para identificar patrones y desencadenantes", type: "cognitive" },
          { text: "Limita el tiempo en la cama a las horas de sueño (no permanezcas en la cama si no puedes dormir)", type: "behavioral" },
          { text: "Practica meditación específica para el sueño (10-15 minutos) antes de acostarte", type: "mindfulness" },
          { text: "Incorpora actividad física regular, pero no justo antes de dormir", type: "physical" },
          { text: "Practica la presencia mindful ante pensamientos ansiosos sobre el sueño", type: "mindfulness" },
          { text: "Evita las siestas largas o después de las 15:00", type: "lifestyle" }
        ],
        moderado: [
          { text: "Consulta con un profesional especializado en medicina del sueño", type: "professional" },
          { text: "Implementa técnicas de restricción del sueño bajo supervisión profesional", type: "behavioral" },
          { text: "Practica técnicas de control de estímulos para asociar la cama solo con el sueño", type: "behavioral" },
          { text: "Realiza meditaciones guiadas específicas para el insomnio antes de dormir (15-20 min)", type: "mindfulness" },
          { text: "Identifica y trabaja preocupaciones o rumiaciones que aparecen a la hora de dormir", type: "cognitive" },
          { text: "Aprende técnicas de reestructuración cognitiva para creencias disfuncionales sobre el sueño", type: "cognitive" },
          { text: "Practica la aceptación radical de las noches de insomnio sin añadir sufrimiento", type: "mindfulness" },
          { text: "Establece una rutina de 'descompresión' de al menos una hora antes de dormir", type: "lifestyle" },
          { text: "Considera técnicas complementarias como la aromaterapia (lavanda) o sonidos", type: "lifestyle" }
        ],
        significativo: [
          { text: "Busca tratamiento especializado en trastornos del sueño", type: "professional" },
          { text: "Sigue rigurosamente un programa de terapia cognitivo-conductual para el insomnio (TCC-I)", type: "professional" },
          { text: "Implementa técnicas de intención paradójica bajo supervisión profesional", type: "cognitive" },
          { text: "Practica diariamente ejercicios de atención plena específicos para el insomnio (20-30 min)", type: "mindfulness" },
          { text: "Trabaja en la desensibilización a la ansiedad relacionada con el sueño", type: "cognitive" },
          { text: "Establece un ritual de preparación para el sueño firmemente estructurado", type: "lifestyle" },
          { text: "Considera la restricción temporal de actividades estimulantes (incluso durante el día)", type: "lifestyle" },
          { text: "Aprende a manejar el impacto emocional del insomnio crónico", type: "emotional" },
          { text: "Sigue estrictamente las recomendaciones médicas sobre tratamiento", type: "professional" },
          { text: "Practica la disociación sueño-despertar (no mirar el reloj durante la noche)", type: "behavioral" }
        ],
        intenso: [
          { text: "Busca atención médica y psicológica especializada urgente para el insomnio severo", type: "professional" },
          { text: "Sigue un programa de tratamiento intensivo bajo supervisión especializada", type: "professional" },
          { text: "Implementa un enfoque integral que incluya TCC-I, mindfulness y posible medicación", type: "professional" },
          { text: "Reorganiza temporalmente tus responsabilidades para priorizar la recuperación del sueño", type: "lifestyle" },
          { text: "Practica técnicas de mindfulness específicas para manejar la desesperación asociada al insomnio crónico", type: "mindfulness" },
          { text: "Trabaja con un profesional para abordar posibles trastornos comórbidos (ansiedad, depresión)", type: "professional" },
          { text: "Aprende a manejar el impacto del insomnio en tu funcionamiento diario", type: "cognitive" },
          { text: "Sigue un programa de rehabilitación del sueño estructurado", type: "professional" },
          { text: "Practica la aceptación radical del proceso de recuperación, que puede ser gradual", type: "mindfulness" },
          { text: "Considera la posibilidad de tratamientos complementarios bajo supervisión", type: "professional" },
          { text: "Establece un sistema de apoyo para los días de mayor dificultad", type: "social" }
        ]
      },
      ruptura_pareja: {
        bajo: [
          { text: "Practica la observación consciente de tus emociones sin juzgarlas", type: "mindfulness" },
          { text: "Mantén tus rutinas diarias y actividades sociales habituales", type: "lifestyle" },
          { text: "Dedica tiempo a actividades que te conecten contigo mismo/a", type: "emotional" },
          { text: "Practica el autocuidado físico como forma de cuidado emocional", type: "physical" },
          { text: "Permite la expresión de emociones en momentos y contextos seguros", type: "emotional" }
        ],
        leve: [
          { text: "Establece límites claros en el contacto con tu ex pareja durante el proceso inicial", type: "social" },
          { text: "Practica la meditación centrada en la autocompasión (10-15 min diarios)", type: "mindfulness" },
          { text: "Crea un espacio y tiempo limitados para procesar emociones, evitando la rumiación constante", type: "cognitive" },
          { text: "Identifica y cuestiona pensamientos distorsionados sobre la ruptura o el futuro", type: "cognitive" },
          { text: "Mantén contacto regular con tu red de apoyo", type: "social" },
          { text: "Incorpora actividad física regular que libere tensión emocional acumulada", type: "physical" },
          { text: "Explora actividades nuevas que amplíen tu identidad más allá de la relación", type: "lifestyle" }
        ],
        moderado: [
          { text: "Considera buscar apoyo profesional con un psicólogo especializado en duelo por ruptura", type: "professional" },
          { text: "Practica diariamente técnicas de mindfulness para manejar pensamientos intrusivos (15-20 min)", type: "mindfulness" },
          { text: "Implementa períodos de 'contacto cero' o comunicación mínima con tu ex pareja", type: "social" },
          { text: "Lleva un diario de emociones y aprendizajes que te permita procesar la experiencia", type: "emotional" },
          { text: "Organiza tu espacio físico eliminando o guardando temporalmente objetos que activen recuerdos intensos", type: "lifestyle" },
          { text: "Establece una rutina diaria estructurada que incluya autocuidado, conexión social y desarrollo personal", type: "lifestyle" },
          { text: "Aprende técnicas específicas para gestionar la ansiedad por separación", type: "emotional" },
          { text: "Identifica patrones relacionales que quieras transformar en futuras relaciones", type: "cognitive" }
        ],
        significativo: [
          { text: "Busca ayuda profesional especializada en duelo por ruptura y apego", type: "professional" },
          { text: "Establece una red de apoyo diario y contactos de seguridad para momentos de crisis", type: "social" },
          { text: "Implementa un 'contacto cero' temporal como parte de tu proceso de recuperación", type: "social" },
          { text: "Practica ejercicios diarios de autocompasión y regulación emocional (20-30 min)", type: "mindfulness" },
          { text: "Trabaja con un profesional para identificar patrones de apego que influyen en tu dolor", type: "professional" },
          { text: "Establece pequeñas metas diarias y semanales, enfocándote en el presente", type: "cognitive" },
          { text: "Desarrolla un plan concreto para manejar recaídas emocionales", type: "emotional" },
          { text: "Busca grupos de apoyo específicos para personas en procesos similares", type: "social" },
          { text: "Practica técnicas de conciencia corporal cuando aparezcan emociones intensas", type: "mindfulness" }
        ],
        intenso: [
          { text: "Busca ayuda profesional inmediata con un especialista en trauma relacional y duelo", type: "professional" },
          { text: "Desarrolla con ayuda profesional un plan de seguridad para momentos de crisis", type: "professional" },
          { text: "Mantén contacto diario con personas de confianza designadas como tu red de apoyo", type: "social" },
          { text: "Practica técnicas de anclaje al presente para momentos de angustia extrema", type: "mindfulness" },
          { text: "Implementa un 'contacto cero' estricto como medida de autoprotección", type: "social" },
          { text: "Divide cada día en pequeñas unidades manejables, con objetivos muy concretos", type: "cognitive" },
          { text: "Trabaja con un profesional en la reconstrucción de tu identidad individual", type: "professional" },
          { text: "Practica el autocuidado básico como acto fundamental de autorespeto", type: "physical" },
          { text: "Establece un sistema de 'primeros auxilios emocionales' para momentos de crisis", type: "emotional" },
          { text: "Considera intervenciones terapéuticas específicas como EMDR si hay trauma relacional", type: "professional" }
        ]
      },
      default: {
        bajo: [
          { text: "Dedica 5-10 minutos diarios a la práctica de atención plena", type: "mindfulness" },
          { text: "Mantén un registro breve de tu bienestar emocional y factores influyentes", type: "emotional" },
          { text: "Practica la observación sin juicio de sensaciones, pensamientos y emociones", type: "mindfulness" },
          { text: "Practica técnicas básicas de autocuidado y conciencia emocional", type: "emotional" },
          { text: "Mantén tus rutinas habituales de actividad física y conexión social", type: "lifestyle" }
        ],
        leve: [
          { text: "Establece una práctica diaria de meditación formal de 10-15 minutos", type: "mindfulness" },
          { text: "Incorpora prácticas diarias breves de relajación o mindfulness (5-10 minutos)", type: "breath" },
          { text: "Identifica situaciones o pensamientos específicos que aumentan tu malestar", type: "cognitive" },
          { text: "Practica la atención plena en actividades cotidianas (comer, caminar, ducharte)", type: "mindfulness" },
          { text: "Mantén una rutina estable de sueño, alimentación y ejercicio", type: "lifestyle" },
          { text: "Busca momentos de disfrute consciente en tu día a día", type: "emotional" }
        ],
        moderado: [
          { text: "Dedica 15-20 minutos diarios a prácticas formales de mindfulness", type: "mindfulness" },
          { text: "Dedica tiempo diario específico para técnicas de regulación emocional", type: "emotional" },
          { text: "Practica la observación sin juicio de pensamientos difíciles, viendo su naturaleza transitoria", type: "mindfulness" },
          { text: "Comparte tus sentimientos con personas de confianza que puedan apoyarte", type: "social" },
          { text: "Establece límites saludables en situaciones que aumentan tu malestar", type: "lifestyle" },
          { text: "Considera buscar apoyo profesional para desarrollar estrategias específicas", type: "professional" },
          { text: "Identifica patrones de pensamiento que contribuyen a tu malestar", type: "cognitive" }
        ],
        significativo: [
          { text: "Busca ayuda profesional con un psicólogo para evaluar tu situación", type: "professional" },
          { text: "Establece una práctica diaria estructurada de mindfulness (20-30 min)", type: "mindfulness" },
          { text: "Desarrolla con ayuda profesional estrategias específicas para tus síntomas", type: "professional" },
          { text: "Practica diariamente ejercicios de autocompasión y aceptación radical", type: "mindfulness" },
          { text: "Equilibra la aceptación de tus emociones con acciones constructivas", type: "emotional" },
          { text: "Prioriza el autocuidado básico como fundamento de tu bienestar", type: "lifestyle" },
          { text: "Comunica claramente tus necesidades a personas cercanas", type: "social" },
          { text: "Evita decisiones importantes o cambios drásticos en este momento", type: "cognitive" }
        ],
        intenso: [
          { text: "Busca ayuda profesional inmediata con especialistas en salud mental", type: "professional" },
          { text: "Practica meditaciones breves de anclaje al momento presente varias veces al día", type: "mindfulness" },
          { text: "Desarrolla un plan de seguridad y apoyo con profesionales", type: "professional" },
          { text: "Identifica recursos de emergencia disponibles para momentos de crisis", type: "professional" },
          { text: "Mantén contacto regular con personas de confianza", type: "social" },
          { text: "Practica ejercicios sencillos de conciencia sensorial para momentos difíciles", type: "mindfulness" },
          { text: "Simplifica tus responsabilidades temporalmente para enfocarte en tu recuperación", type: "lifestyle" },
          { text: "Sigue rigurosamente las recomendaciones del profesional de salud mental", type: "professional" },
          { text: "Recuerda que con el apoyo adecuado, la recuperación es posible", type: "emotional" }
        ]
      }
    };

    // Verificar si existe el área específica, si no, usar default
    const selectedArea = recommendationsByArea[symptomType] ? recommendationsByArea[symptomType] : recommendationsByArea.default;
    
    // Devolver las recomendaciones para el nivel correspondiente
    return selectedArea[sufferingLevel];
  };
  
  // Agrupar las recomendaciones por tipo para mejorar la visualización
  const groupedRecommendations = {
    mindfulness: symptomSpecificContent.filter(r => r.type === 'mindfulness'),
    breath: symptomSpecificContent.filter(r => r.type === 'breath'),
    physical: symptomSpecificContent.filter(r => r.type === 'physical'),
    cognitive: symptomSpecificContent.filter(r => r.type === 'cognitive'),
    emotional: symptomSpecificContent.filter(r => r.type === 'emotional'),
    social: symptomSpecificContent.filter(r => r.type === 'social'),
    lifestyle: symptomSpecificContent.filter(r => r.type === 'lifestyle'),
    professional: symptomSpecificContent.filter(r => r.type === 'professional'),
    sleep: symptomSpecificContent.filter(r => r.type === 'sleep')
  };
  
  // Eliminar grupos vacíos
  Object.keys(groupedRecommendations).forEach(key => {
    if (groupedRecommendations[key].length === 0) {
      delete groupedRecommendations[key];
    }
  });
  
  // Nombres legibles para los grupos
  const groupNames = {
    mindfulness: 'Atención Plena',
    breath: 'Respiración Consciente',
    physical: 'Actividad Física',
    cognitive: 'Estrategias Cognitivas',
    emotional: 'Regulación Emocional',
    social: 'Conexión Social',
    lifestyle: 'Estilo de Vida',
    professional: 'Apoyo Profesional',
    sleep: 'Higiene del Sueño'
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };
  
  // Esta función fue comentada porque no se está utilizando actualmente
  // Si necesitas añadir descripciones específicas por área en el futuro, descomenta esta función
  /*
  const getAreaSpecificDescription = () => {
    const areaDescriptions = {
      TRISTEZA: "Basado en tu evaluación de tristeza, estas estrategias podrían ayudarte a mejorar tu estado de ánimo:",
      ANSIEDAD: "Basado en tu evaluación de ansiedad, estas estrategias podrían ayudarte a reducir la intensidad de los síntomas:",
      ESTRES: "Basado en tu evaluación de estrés, estas técnicas podrían ayudarte a recuperar el equilibrio:",
      AUTOESTIMA: "Basado en tu evaluación de autoestima, estas prácticas podrían fortalecer tu valoración personal:",
      INSOMNIO: "Basado en tu evaluación de problemas de sueño, estas estrategias podrían mejorar tu descanso:",
      RUPTURA_PAREJA: "Basado en tu evaluación de malestar por ruptura, estas estrategias pueden acompañar tu proceso:"
    };
    
    return areaDescriptions[areaId] || "Basado en tu evaluación, estas estrategias podrían ayudarte a reducir tu nivel de malestar:";
  };
  */
  
  return (
    <div className="recommendations-container">
      <div className="recommendations-intro">
        <p className="intro-text">
          <strong>Recomendaciones personalizadas</strong>
        </p>
      </div>
      
      <div className="mindfulness-intro">
        <div className="intro-icon">{recommendationIcons.mindfulness}</div>
        <div className="intro-content">
          <h4>Enfoque basado en Mindfulness</h4>
        </div>
      </div>
      
      <motion.div 
        className="recommendations-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Mostrar primero las recomendaciones de mindfulness, si existen */}
        {groupedRecommendations.mindfulness && groupedRecommendations.mindfulness.length > 0 && (
          <motion.div 
            className="recommendation-group mindfulness-group"
            variants={itemVariants}
          >
            <div className="group-header" style={{ backgroundColor: `${color}20`, borderColor: color }}>
              <span className="group-icon" style={{ color: color }}>{recommendationIcons.mindfulness}</span>
              <h4>{groupNames.mindfulness}</h4>
            </div>
            
            <ul className="group-items">
              {groupedRecommendations.mindfulness.map((recommendation, idx) => (
                <motion.li 
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 * idx }}
                >
                  {recommendation.text}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
        
        {/* Priorizar mostrar las recomendaciones profesionales en segundo lugar si existen */}
        {groupedRecommendations.professional && groupedRecommendations.professional.length > 0 && (
          <motion.div 
            className="recommendation-group professional-group"
            variants={itemVariants}
          >
            <div className="group-header" style={{ backgroundColor: `${color}20`, borderColor: color }}>
              <span className="group-icon" style={{ color: color }}>{recommendationIcons.professional}</span>
              <h4>{groupNames.professional}</h4>
            </div>
            
            <ul className="group-items">
              {groupedRecommendations.professional.map((recommendation, idx) => (
                <motion.li 
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 * idx }}
                >
                  {recommendation.text}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
        
        {/* Mostrar el resto de grupos */}
        {Object.keys(groupedRecommendations)
          .filter(key => key !== 'mindfulness' && key !== 'professional') // Excluir los grupos ya mostrados
          .map((groupKey) => (
            groupedRecommendations[groupKey].length > 0 && (
              <motion.div 
                key={groupKey} 
                className={`recommendation-group ${groupKey}-group`}
                variants={itemVariants}
              >
                <div className="group-header" style={{ backgroundColor: `${color}20`, borderColor: color }}>
                  <span className="group-icon" style={{ color: color }}>{recommendationIcons[groupKey]}</span>
                  <h4>{groupNames[groupKey]}</h4>
                </div>
                
                <ul className="group-items">
                  {groupedRecommendations[groupKey].map((recommendation, idx) => (
                    <motion.li 
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 * idx }}
                    >
                      {recommendation.text}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )
          ))
        }
      </motion.div>
      
      <div className="mindfulness-benefits">
        <h4>Beneficios del Mindfulness para tu bienestar</h4>
        <div className="benefits-grid">
          <div className="benefit-item">
            <div className="benefit-icon">🧠</div>
            <div className="benefit-text">
              <h5>Reducción del Estrés</h5>
              <p>La práctica regular reduce los niveles de cortisol y activa la respuesta de relajación del cuerpo.</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">🫀</div>
            <div className="benefit-text">
              <h5>Regulación Emocional</h5>
              <p>Mejora tu capacidad para gestionar emociones difíciles sin ser abrumado por ellas.</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">💭</div>
            <div className="benefit-text">
              <h5>Claridad Mental</h5>
              <p>Reduce la rumiación y aumenta la capacidad de enfoque y toma de decisiones.</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">❤️</div>
            <div className="benefit-text">
              <h5>Autocompasión</h5>
              <p>Desarrolla una relación más amable y comprensiva contigo mismo/a en momentos difíciles.</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="recommendation-disclaimer">
        <div className="disclaimer-icon">
          <span>i</span>
        </div>
        <p>Recuerda que estas recomendaciones son generales. Para un plan personalizado, consulta con un profesional.</p>
      </div>
    </div>
  );
};

export default Recommendations;
